import { FC, useEffect, useMemo, useRef, useState } from "react";
import AddGoal from "@pages/Program/components/goal/AddGoal";
import {
  GoalItem,
  MilestoneDisplay,
  ProgramPayload,
} from "@shared/interfaces/ProgramFiles";
import CalendarTimeline from "@pages/Program/components/calendar/CalendarTimeline";
import GoalTable from "@pages/Program/components/goal/GoalTable";
import AddMilestone from "@pages/Program/components/milestones/AddMilestone";
import LatestUpdatesPanel, {
  LatestUpdate,
} from "@pages/Program/components/calendar/LatestUpdatesPanel";
import UpdateDetailsModal from "@pages/Program/components/UpdateDetailsModal";
import { useProgram } from "@pages/Program/utils/useProgram";
import { useParams, useSearchParams } from "react-router-dom";
import { useMilestone } from "@pages/Program/components/milestones/utils/useMilestone";
import { useGoal } from "@pages/Program/components/goal/utils/useGoal";
import SkeletonCalendarTimeline from "@pages/Program/components/calendar/skeleton/SkeletonCalendarTimeline";
import SkeletonGoalTable from "@pages/Program/components/goal/skeleton/SkeletonGoalTable";
import SkeletonLatestUpdates from "@pages/Program/components/calendar/skeleton/SkeletonLatestUpdates";

import { addMonths, startOfMonth, endOfMonth } from "date-fns";
import CalendarModal from "./components/calendar/CalendarModal";
import AvatarList from "@pages/Home/components/AvatarList";
import PeopleWithAccessPopup from "./components/PeopleWithAccessPopup";
import SubTitleHeader from "@shared/components/SubTitleHeader";

const ProgramDetails: FC = () => {
  const [isAddGoalOpen, setIsAddGoalOpen] = useState(false);
  const [isAddMilestoneOpen, setIsAddMilestoneOpen] = useState(false);

  const openMilestonePopup = () => setIsAddMilestoneOpen(true);

  const calendarRef = useRef<HTMLDivElement>(null);
  const [calendarHeight, setCalendarHeight] = useState<number>(0);

  const [isUpdatesDetailModalOpen, setUpdatesDetailModalOpen] = useState(false);
  const [milestones, setMilestones] = useState<MilestoneDisplay[]>([]);
  const [goals, setGoals] = useState<GoalItem[]>([]);
  const [upcomingItems, setUpcomingItems] = useState<LatestUpdate[]>([]);

  const { getProgramById, getProgramsLatestUpdates } = useProgram();
  const { fetchGoalsByProgram } = useGoal();
  const { fetchProgramMilestones } = useMilestone();

  const [isMilestoneDataLoading, setIsMilestoneDataLoading] = useState(true);
  const [isGoalsDataLoading, setIsGoalsDataLoading] = useState(true);
  const [isProgramDataLoading, setIsProgramDataLoading] = useState(true);
  const [isUpdatesDataLoading, setIsUpdatesDataLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const openCalendar = () => setIsCalendarOpen(true);

  const [selectedUpdate, setSelectedUpdate] = useState<LatestUpdate>(
    {} as LatestUpdate
  );

  const { programId } = useParams();

  const MONTHS_TO_SHOW = 2;
  const [msViewStart, setMsViewStart] = useState(
    startOfMonth(addMonths(new Date(), -1))
  );
  const msViewEnd = useMemo(
    () => endOfMonth(addMonths(msViewStart, MONTHS_TO_SHOW - 1)),
    [msViewStart]
  );

  const [program, setProgram] = useState<ProgramPayload | null>(null);

  const openUpdatesDetailModal = () => {
    setUpdatesDetailModalOpen(true);
  };

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === calendarRef.current) {
          setCalendarHeight(entry.contentRect.height);
        }
      }
    });

    if (calendarRef.current) {
      observer.observe(calendarRef.current);
    }

    return () => {
      if (calendarRef.current) {
        observer.unobserve(calendarRef.current);
      }
    };
  }, []);

  const fetchMilestone = async () => {
    if (programId) {
      try {
        setIsMilestoneDataLoading(true);
        const data = await fetchProgramMilestones(Number(programId), {
          start_date: msViewStart.toISOString().split("T")[0],
          end_date: msViewEnd.toISOString().split("T")[0],
        });
        setMilestones(data.milestones);
      } catch (err) {
        console.error("Failed to fetch milestones:", err);
      } finally {
        setIsMilestoneDataLoading(false);
      }
    }
  };

  const fetchGoals = async () => {
    if (programId) {
      try {
        setIsGoalsDataLoading(true);
        const goals = await fetchGoalsByProgram(programId);
        setGoals(goals.goals);
      } catch (err) {
        console.error("Failed to fetch goals:", err);
      } finally {
        setIsGoalsDataLoading(false);
      }
    }
  };

  const fetchLatestUpdates = async () => {
    if (programId)
      try {
        setIsUpdatesDataLoading(true);
        const response = await getProgramsLatestUpdates(programId);
        setUpcomingItems(response || []);
      } catch (error) {
        console.error("Failed to fetch latest updates", error);
      } finally {
        setIsUpdatesDataLoading(false);
      }
  };

  useEffect(() => {
    const fetchProgram = async () => {
      if (!programId) return;
      try {
        setIsProgramDataLoading(true);
        const data = await getProgramById(Number(programId));
        setProgram(data);
      } catch (err) {
        console.error("Failed to fetch program:", err);
      } finally {
        setIsProgramDataLoading(false);
      }
    };

    fetchProgram();
    fetchGoals();
  }, [programId]);

  useEffect(() => {
    fetchLatestUpdates();
  }, [programId]);

  useEffect(() => {
    fetchMilestone();
  }, [msViewStart, programId]);

  const [searchParams] = useSearchParams();

  const milestoneId = searchParams.get("milestoneId");

  const updateMilestonesAndGoals = () => {
    fetchMilestone();
    fetchGoals();
  };

  useEffect(() => {
    if (milestoneId && milestoneId === "new") {
      setIsAddMilestoneOpen(true);
    }
  }, [milestoneId]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!program) return null;

  return (
    <div className="flex-1 flex flex-col px-8">
      <SubTitleHeader
        title={`${isProgramDataLoading ? "Loading..." : program?.name}`}
        subtitleStyle="text-gray-400 text-sm 2xl:text-base"
        spacer={true}
      >
        <div className="flex space-x-5 ml-[-15px] ">
          <div className="relative">
            <AvatarList
              shared_with={program.members.map(
                (member: any, index: number) => ({
                  id: member.user.id,
                  role_name: "",
                  user_email: member.user.email,
                  first_name: member.user.first_name,
                  last_name: member.user.last_name,
                  ordering: member.leader ? 1 : 3,
                })
              )}
              size={10}
              onClick={() => setShowPopup(true)}
            />

            {showPopup && (
              <PeopleWithAccessPopup
                members={program.members}
                onClose={() => setShowPopup(false)}
              />
            )}
          </div>

          <div className="relative">
            <div className="inline-flex rounded-md" role="group">
              <button
                type="button"
                className={`bg-blue-600 text-white ${
                  goals.length > 0 ? "rounded-s-lg" : "rounded-lg"
                } hover:bg-blue-700 transition text-sm 2xl:text-base w-[5rem] 2xl:w-[6rem] py-2 flex justify-center items-center 2xl:space-x-2 space-x-1`}
                onClick={() => setIsAddGoalOpen(true)}
              >
                <span>+</span>
                <span className="">Goal</span>
              </button>
              {goals.length > 0 && (
                <button
                  type="button"
                  className="px-4 py-3 text-sm 2xl:text-base font-medium text-white bg-blue-600 rounded-e-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-400"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDropdownOpen((prev) => !prev);
                  }}
                >
                  <img
                    src="/arrow_dropdown.svg"
                    className="w-3"
                    alt="Dropdown Arrow"
                  />
                </button>
              )}
            </div>

            {isDropdownOpen && (
              <div
                ref={dropdownRef}
                className="absolute right-0 mt-2 w-32 2xl:w-36 bg-white shadow-md rounded-md border z-50"
              >
                <button
                  onClick={() => setIsAddMilestoneOpen(true)}
                  className="w-full text-sm 2xl:text-base text-left px-4 py-2 text-gray-800 hover:bg-gray-100 2xl:space-x-2 space-x-1 flex"
                >
                  <span>+</span>
                  <span className="">Task</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </SubTitleHeader>
      {goals.length == 0 && !isGoalsDataLoading ? (
        <div className="mt-6 w-full h-full flex flex-col flex-1 items-center justify-center text-sm 2xl:text-base">
          <img
            src="/goal.svg" // replace with your actual image path
            alt="Target"
            className="w-76 mb-6"
          />
          <h2 className="text-base 2xl:text-lg font-semibold text-gray-800 mb-2">
            Let’s define your goals
          </h2>
          <p className="text-gray-600 max-w-lg mb-6 text-center">
            You’ve created a new program. Now it’s time to define the goals that
            will guide your work and measure your progress.
          </p>
          <button
            onClick={() => setIsAddGoalOpen(true)}
            className="bg-blue-600 text-white font-semibold px-6 py-2 rounded hover:bg-blue-700 transition mb-4"
          >
            Add First Goal
          </button>
          <div className="relative group inline-block">
            <button className="text-sm text-blue-600 hover:underline relative z-10">
              What’s a goal?
            </button>
            <div className="absolute bottom-[125%] left-1/2 -translate-x-1/2 w-64 p-3 bg-gray-800 text-white text-xs rounded-md opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-300 z-0">
              A goal is a specific, measurable outcome that helps you track your
              program’s success. It guides your efforts and helps evaluate
              progress.
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-6 w-[100%] h-full flex flex-col  space-y-4">
          <div
            className="flex justify-between space-x-4 min-h-[390px] max-[390px] 2xl:min-h-[430px] 2xl:max-h-[430px]"
            style={{ height: calendarHeight }}
          >
            <div className="w-[65%]">
              {isMilestoneDataLoading ? (
                <SkeletonCalendarTimeline />
              ) : (
                <CalendarTimeline
                  ref={calendarRef}
                  events={milestones}
                  msViewStart={msViewStart}
                  setMsViewStart={setMsViewStart}
                  msViewEnd={msViewEnd}
                  openMilestonePopup={openMilestonePopup}
                  openCalendar={openCalendar}
                  fetchMilestone={updateMilestonesAndGoals}
                  startDate={program.start_date}
                  endDate={program.end_date}
                />
              )}
            </div>

            {isUpdatesDataLoading ? (
              <SkeletonLatestUpdates />
            ) : (
              <LatestUpdatesPanel
                openUpdatesDetailModal={openUpdatesDetailModal}
                setSelectedUpdate={setSelectedUpdate}
                upcomingItems={upcomingItems}
                fetchLatestUpdates={fetchLatestUpdates}
              />
            )}
          </div>

          <div className="flex-1 w-ful h-[380px] pb-4">
            <div className="flex w-full space-x-4 h-[380px]">
              <div className="w-[100%]">
                {isGoalsDataLoading ? (
                  <SkeletonGoalTable />
                ) : (
                  <GoalTable
                    goals={goals}
                    fetchGoals={fetchGoals}
                    startDate={program.start_date}
                    endDate={program.end_date}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <AddGoal
        isOpen={isAddGoalOpen}
        onClose={() => setIsAddGoalOpen(false)}
        fetchGoals={fetchGoals}
        startDate={program.start_date}
        endDate={program.end_date}
      />

      <AddMilestone
        isOpen={isAddMilestoneOpen}
        onClose={() => {
          setIsAddMilestoneOpen(false);
        }}
        fetchMilestone={updateMilestonesAndGoals}
        startDate={program.start_date}
        endDate={program.end_date}
      />

      {selectedUpdate && (
        <UpdateDetailsModal
          isOpen={isUpdatesDetailModalOpen}
          onClose={() => {
            setUpdatesDetailModalOpen(false);
            setSelectedUpdate({} as LatestUpdate);
            fetchLatestUpdates();
          }}
          selectedUpdate={selectedUpdate}
          refreshProgramData={() => {
            updateMilestonesAndGoals();
          }}
        />
      )}

      <CalendarModal
        isOpen={isCalendarOpen}
        onClose={() => setIsCalendarOpen(false)}
        startDate={program.start_date}
        endDate={program.end_date}
      />
    </div>
  );
};

export default ProgramDetails;
